import {
  AIOnboardingModalSuggestionKinds,
  AIOnboardingModalVariant,
  AIOnboardingModalVariantConfig,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import { AIOnboardingModalFormShape } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { EntityListFragmentDoc } from '@/modules/entities/graphql/ListClientEntities.generated';
import { EstateOnboardingUnacknowledgedSuggestionsV2Document } from '@/modules/estate/estateAIOnboarding/graphql/EstateOnboardingUnacknowledgedSuggestionsV2.generated';
import { ROUTE_KEYS } from '@/navigation/constants';
import { AiSuggestionKind } from '@/types/schema';
import { pluralize } from '@/utils/formatting/strings';

export const AI_ONBOARDING_MODAL_REVIEW_ALL_PAGE_SIZE = 50;

export const AI_ONBOARDING_MODAL_VARIANT_CONFIGS: Record<
  AIOnboardingModalVariant,
  AIOnboardingModalVariantConfig
> = {
  [AIOnboardingModalVariant.ALL_ENTITIES]: {
    variant: AIOnboardingModalVariant.ALL_ENTITIES,
    getHeaderText: (count: number) =>
      pluralize({
        count,
        singular: 'entity',
        plural: 'entities',
        includeCount: true,
      }),
    suggestionKinds: [
      AiSuggestionKind.OnboardingEntity,
      AiSuggestionKind.OnboardingTestamentaryEntity,
    ],
    formPaths: ['entities', 'testamentaryEntities'],
    onCompleteNavigationRoute: ROUTE_KEYS.HOUSEHOLD_ESTATE_DIRECT_HOLDINGS,
    refetchQueries: [
      EntityListFragmentDoc,
      EstateOnboardingUnacknowledgedSuggestionsV2Document,
    ],
  },
  [AIOnboardingModalVariant.TRUST_ENTITIES]: {
    variant: AIOnboardingModalVariant.TRUST_ENTITIES,
    getHeaderText: (count: number) =>
      pluralize({
        count,
        singular: 'entity',
        plural: 'entities',
        includeCount: true,
      }),
    suggestionKinds: [
      AiSuggestionKind.OnboardingEntity,
      AiSuggestionKind.OnboardingTestamentaryEntity,
    ],
    formPaths: ['entities', 'testamentaryEntities'],
    onCompleteNavigationRoute: ROUTE_KEYS.HOUSEHOLD_ESTATE_TRUSTS,
    refetchQueries: [
      EntityListFragmentDoc,
      EstateOnboardingUnacknowledgedSuggestionsV2Document,
    ],
  },
  [AIOnboardingModalVariant.BUSINESS_ENTITIES]: {
    variant: AIOnboardingModalVariant.BUSINESS_ENTITIES,
    getHeaderText: (count: number) =>
      pluralize({
        count,
        singular: 'entity',
        plural: 'entities',
        includeCount: true,
      }),
    suggestionKinds: [
      AiSuggestionKind.OnboardingEntity,
      AiSuggestionKind.OnboardingTestamentaryEntity,
    ],
    formPaths: ['entities'],
    onCompleteNavigationRoute: ROUTE_KEYS.HOUSEHOLD_ESTATE_BUSINESSES,
    refetchQueries: [
      EntityListFragmentDoc,
      EstateOnboardingUnacknowledgedSuggestionsV2Document,
    ],
  },
  [AIOnboardingModalVariant.HOLDINGS]: {
    variant: AIOnboardingModalVariant.HOLDINGS,
    getHeaderText: (count: number) =>
      pluralize({
        count,
        singular: 'holding',
        plural: 'holdings',
        includeCount: true,
      }),
    suggestionKinds: [
      AiSuggestionKind.Asset,
      AiSuggestionKind.Liability,
      AiSuggestionKind.LifeInsurance,
    ],
    formPaths: ['assets', 'liabilities', 'lifeInsurancePolicies'],
    onCompleteNavigationRoute: ROUTE_KEYS.HOUSEHOLD_ESTATE_BALANCE_SHEET,
    refetchQueries: [], // TODO: add query to refetch when implementing holdings suggestions
  },
};

export const AI_ONBOARDING_MODAL_SUGGESTION_FORM_KEY_MAP: Record<
  AIOnboardingModalSuggestionKinds,
  keyof AIOnboardingModalFormShape
> = {
  [AiSuggestionKind.OnboardingIndividual]: 'individuals',
  [AiSuggestionKind.OnboardingOrganization]: 'organizations',
  [AiSuggestionKind.OnboardingEntity]: 'entities',
  [AiSuggestionKind.OnboardingTestamentaryEntity]: 'testamentaryEntities',
  [AiSuggestionKind.Asset]: 'assets',
  [AiSuggestionKind.Liability]: 'liabilities',
  [AiSuggestionKind.LifeInsurance]: 'lifeInsurancePolicies',
};
